import React, { createContext } from 'react';
import { submitCitationStream } from '@/services/genaiQueriesV2';
import { StreamController } from '@/classes/StreamController';

export const RUNNING_STATUS = {
  INACTIVE: 'inactive',
  RUNNING: 'running',
  COMPLETED: 'completed',
  REFINING: 'refining',
  ERROR: 'error',
};

export const CITATIONS_STATUS = {
  INACTIVE: 'inactive',
  RUNNING: 'running',
  COMPLETED: 'completed',
  ERROR: 'error',
};

const initialState = {
  resultId: null,
  queryRunId: null,
  answer: null,
  plan: null,
  context: null,
  sources: null,
  fullContext: null,
  runningStatus: RUNNING_STATUS.INACTIVE,

  citationsResultId: null,
  citationResult: null,
  citationsContext: null,
  citationsStreamEnded: false,
  citationsStatus: CITATIONS_STATUS.INACTIVE,
};

const REDUCER_ACTIONS = {
  open: 'open',
  messageReceived: 'messageReceived',
  errorReceived: 'errorReceived',
  refiningStart: 'refiningStart',
  complete: 'complete',
  reset: 'reset',
};

const CITATION_REDUCER_ACTIONS = {
  open: 'citations_open',
  messageReceived: 'citations_messageReceived',
  errorReceived: 'citations_errorReceived',
  refiningStart: 'citations_refiningStart',
  complete: 'citations_complete',
  reset: 'citations_reset',
};

const reducer = (state, action) => {
  switch (action.type) {
    case REDUCER_ACTIONS.open: {
      return { ...state, runningStatus: RUNNING_STATUS.RUNNING, resultId: action.payload };
    }
    case REDUCER_ACTIONS.errorReceived: {
      return { ...state, error: action.payload, runningStatus: RUNNING_STATUS.ERROR };
    }
    case REDUCER_ACTIONS.refiningStart: {
      return { ...state, runningStatus: RUNNING_STATUS.REFINING };
    }
    case REDUCER_ACTIONS.reset: {
      return initialState;
    }
    case REDUCER_ACTIONS.messageReceived: {
      return { ...state, answer: action.payload };
    }
    case REDUCER_ACTIONS.complete: {
      const { payload } = action;
      const { answer, queryRunId, plan, context, fullContext, sources } = payload;
      return { ...state, runningStatus: RUNNING_STATUS.COMPLETED, answer, queryRunId, context, plan, fullContext, sources };
    }

    case CITATION_REDUCER_ACTIONS.open: {
      return {
        ...state,
        citationsResultId: action.payload,
        citationsStatus: CITATIONS_STATUS.RUNNING,
        citationsStreamEnded: false,
      };
    }
    case CITATION_REDUCER_ACTIONS.messageReceived: {
      return { ...state, citationResult: action.payload };
    }
    case CITATION_REDUCER_ACTIONS.errorReceived: {
      return { ...state, citationsStatus: CITATIONS_STATUS.ERROR };
    }
    case CITATION_REDUCER_ACTIONS.complete: {
      const { answer, context } = action.payload;
      return {
        ...state,
        citationResult: answer,
        citationsContext: context,
        citationsStreamEnded: true,
        citationsStatus: CITATIONS_STATUS.COMPLETED,
      };
    }
    case CITATION_REDUCER_ACTIONS.reset: {
      return {
        ...state,
        citationsResultId: null,
        citationResult: null,
        citationsContext: null,
        citationsStreamEnded: false,
        citationsStatus: CITATIONS_STATUS.INACTIVE,
      };
    }

    default: {
      return state;
    }
  }
};

export const ResultContext = createContext(null);

export const ResultContextProvider = ({ resultId, isAgentScriptResult = false, children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const streamSource = React.useRef(null);
  const citationsStreamSource = React.useRef(null);

  React.useEffect(() => {
    const streamOptions = { includeContext: true, includeAgentPlan: isAgentScriptResult };
    new StreamController(resultId, streamOptions, streamSource, dispatch, REDUCER_ACTIONS, isAgentScriptResult);
    return () => {
      streamSource?.current?.close();
      citationsStreamSource.current?.close();
      dispatch({ type: REDUCER_ACTIONS.reset });
      dispatch({ type: CITATION_REDUCER_ACTIONS.reset });
    };
  }, [resultId]);

  const getCitations = React.useCallback(async (queryRunId) => {
    if (!queryRunId) {
      dispatch({ type: CITATION_REDUCER_ACTIONS.errorReceived, payload: 'Missing query_run_id' });
      return;
    }
    if (citationsStreamSource.current) {
      citationsStreamSource.current.close();
    }
    dispatch({ type: CITATION_REDUCER_ACTIONS.reset });
    const result = await submitCitationStream({ queryRunId });
    if (result.status === 200 && !result.error && result.runId) {
      new StreamController(
        result.runId,
        { includeContext: true },
        citationsStreamSource,
        dispatch,
        CITATION_REDUCER_ACTIONS
      );
    } else {
      dispatch({ type: CITATION_REDUCER_ACTIONS.errorReceived, payload: result.error });
    }
  }, []);

  React.useEffect(() => {
    return () => {
      if (citationsStreamSource.current) {
        citationsStreamSource.current.close();
      }
    };
  }, []);

  const value = React.useMemo(() => ({ ...state, getCitations }), [state, getCitations]);

  return <ResultContext.Provider value={value}>{children}</ResultContext.Provider>;
};
